import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';

interface ServiceTile {
  readonly name: string;
  readonly status: 'healthy' | 'degraded' | 'down' | 'unknown';
  readonly latencyP95Ms: number | null;
  readonly errorRatePct: number | null;
  readonly requestsPerMin: number | null;
  readonly revision: string | null;
}

interface Response {
  readonly configured: boolean;
  readonly services: readonly ServiceTile[];
  readonly fetchedAt: string;
}

function dotClass(status: ServiceTile['status']): string {
  if (status === 'healthy') return 'nw-status-dot';
  if (status === 'degraded') return 'nw-status-dot nw-status-dot--warn';
  if (status === 'down') return 'nw-status-dot nw-status-dot--err';
  return 'nw-status-dot nw-status-dot--warn';
}

export function Infra() {
  const query = useQuery({
    queryKey: queryKeys.infra(),
    queryFn: async () => {
      const res = await apiClient.get<Response>('/owner/infra');
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Infra</h1>
        <p className="nw-page-header__subtitle">
          Health probe across every NewsWave Cloud Run service. Refreshes every minute.
        </p>
      </header>

      {data && !data.configured && (
        <div className="nw-placeholder">
          <h2 className="nw-placeholder__title">Cloud Run not configured</h2>
          <p>
            Set GCP_PROJECT_ID + GCP_REGION env vars on admin-api so this
            page can call the run.googleapis.com control plane for each
            service's readiness condition. Latency p95 + 5xx rate require
            a follow-up that wires in the Monitoring API time-series.
          </p>
        </div>
      )}

      <div className="nw-status-grid">
        {(data?.services ?? []).map((s) => (
          <div className="nw-status-tile" key={s.name}>
            <div className="nw-status-tile__head">
              <span className={dotClass(s.status)} />
              {s.name}
            </div>
            <div className="nw-status-tile__metric">
              {s.status === 'unknown'
                ? '—'
                : s.status === 'down'
                  ? 'Unreachable'
                  : s.status === 'degraded'
                    ? 'Not ready'
                    : 'Ready'}
            </div>
            {s.revision && (
              <div className="nw-status-tile__meta">{s.revision}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
